/** @jsxImportSource @emotion/react */

import AdCarouselWithText from '@stories/organisms/AdCarouselWithText';
import { Default as AdCarouselWithTextStory } from '@stories/organisms/AdCarouselWithText/AdCarouselWithText.stories';
import RatingCarousel from '@stories/organisms/RatingCarousel';
import { media } from '@stories/theming/settings';

import CustomersBanner from './CustomersBanner';
import { Guarantee } from './Guarantee';
import { Logo } from './Logo';
import { Objectives } from './Objectives';
import { OurStory } from './OurStory';
import { Overview } from './Overview';
import { Process } from './Process';
import { Quality } from './Quality';
import { Variety } from './Variety';
import { OverviewProps, RatingCarouselProps } from './VitafyAboutUsPageData';

import type { TTrustedShopsRatingResponse } from '@vitafy/storefront-api-contracts';

export type VitafyAboutUsPageProps = {
  etrustedData?: TTrustedShopsRatingResponse | null;
};

export const gradientBackground = {
  background: 'linear-gradient(180deg, hsl(199 94% 36%) 0%, hsl(199 94% 14%) 100%)'
};

export const section = {
  margin: 'auto',
  maxWidth: '1400px',
  padding: ['3rem var(--offset)']
};

export const headlines = {
  h1: {
    fontSize: '46px',
    textTransform: 'uppercase',
    fontWeight: '900',
    lineHeight: '1.1',
    [media('desktop')]: {
      fontSize: '56px'
    }
  } as const,
  h2: {
    fontSize: '42px',
    fontWeight: '900',
    lineHeight: '1.1',
    textTransform: 'uppercase',
    [media('desktop')]: {
      fontSize: '48px'
    }
  } as const,
  h3: {
    fontSize: '34px',
    fontWeight: '900',
    lineHeight: '1.1',
    textTransform: 'uppercase',
    [media('desktop')]: { fontSize: '38px' }
  } as const,
  h4: {
    fontSize: '28px',
    fontWeight: 'bold',
    lineHeight: '1.1',
    textTransform: 'uppercase',
    [media('desktop')]: { fontSize: '32px' }
  } as const
};

export const colors = {
  copy: 'hsl(0 0% 40%)'
};

export const SectionHeader: React.FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => {
  return (
    <header css={{ textAlign: 'center' }}>
      <h2 css={headlines.h3}>{title}</h2>
      <div
        css={{
          color: colors.copy,
          display: 'block',
          fontWeight: '400',
          margin: '2rem auto',
          maxWidth: '70rem',
          lineHeight: '1.6'
        }}
        dangerouslySetInnerHTML={{ __html: subtitle }}
      />
    </header>
  );
};

export const VitafyAboutUsPage: React.FC<VitafyAboutUsPageProps> = ({ etrustedData }) => {
  return (
    <div
      css={{
        fontSize: '20px',
        fontWeight: '100',
        [media('desktop')]: { fontSize: '30px' }
      }}
    >
      <AdCarouselWithText {...AdCarouselWithTextStory.args} etrustedData={etrustedData} />
      <RatingCarousel {...RatingCarouselProps} fontSize="18px" />
      <CustomersBanner />
      <Overview {...OverviewProps} />
      <Logo />
      <OurStory />
      <Quality />
      <Variety />
      <Objectives />
      <Guarantee />
      <Process />
    </div>
  );
};
